/**
 * Utility functions for calculating electricity subsidies in Norway
 */
import { PriceArea } from '../types';

// Strømstøtte threshold per year (NOK/kWh, excl. VAT). Source: regjeringen.no/strømtiltak.
// 2027 (79 øre) is only proposed in statsbudsjettet 2027 — add it once Stortinget has adopted it.
const SUBSIDY_THRESHOLD_EX_VAT_BY_YEAR: Record<number, number> = {
  2025: 0.75,
  2026: 0.77,
};
export const SUBSIDY_PERCENTAGE = 0.90; // 90%
export const SUBSIDY_MONTHLY_KWH_CAP = 5000;

// Norgespris (NOK/kWh, excl. VAT), price period 1.10.2025–31.12.2026
export const NORGESPRIS_EX_VAT = 0.40;
export const NORGESPRIS_MONTHLY_KWH_CAP = 5000;
export const NORGESPRIS_CABIN_MONTHLY_KWH_CAP = 1000;

/**
 * Household electricity in Nordland, Troms and Finnmark is exempt from VAT.
 * We use price area NO4 as an approximation for that region.
 */
export const getVatMultiplier = (area?: PriceArea) => (area === 'NO4' ? 1 : 1.25);

export const getSubsidyThresholdExVat = (date: Date = new Date()) => {
  const years = Object.keys(SUBSIDY_THRESHOLD_EX_VAT_BY_YEAR).map(Number).sort((a, b) => a - b);
  const year = years.filter(y => y <= date.getFullYear()).pop() ?? years[0];
  return SUBSIDY_THRESHOLD_EX_VAT_BY_YEAR[year];
};

export const getSubsidyThreshold = (area?: PriceArea, date: Date = new Date()) =>
  getSubsidyThresholdExVat(date) * getVatMultiplier(area);

export const getNorgesprisPrice = (area?: PriceArea) => NORGESPRIS_EX_VAT * getVatMultiplier(area);

/**
 * Calculate the subsidy amount and price after subsidy based on the current electricity price
 * @param price - The current electricity price as shown to the user, i.e. incl. VAT where VAT applies (NOK/kWh)
 * @param area - Price area, used to decide whether VAT applies (NO4 is VAT exempt)
 * @returns Object with subsidy amount and price after subsidy
 */
export const calculateElectricitySubsidy = (price: number, area?: PriceArea) => {
  const threshold = getSubsidyThreshold(area);

  // Initialize variables
  let subsidyAmount = 0;
  let priceAfterSubsidy = price;
  let isSubsidyActive = false;

  // Check if price is above threshold (both on the same VAT basis)
  if (price > threshold) {
    const amountAboveThreshold = price - threshold;
    subsidyAmount = amountAboveThreshold * SUBSIDY_PERCENTAGE;
    priceAfterSubsidy = price - subsidyAmount;
    isSubsidyActive = true;
  }

  return {
    subsidyAmount,
    priceAfterSubsidy,
    isSubsidyActive
  };
};
